"use client";

import Translations from "@translate/components/OnboardingWizard/PlanOverviewModal/PlanFitnessImpactStrip";
import type { FC } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { I18nUtil } from "@utils/i18n";
import { FitnessRadarChart } from "@components/fitness/RadarChart/FitnessRadarChart";
import type { OnboardingV3PlanImpact } from "@/src/hooks/onboarding/onboardingV3Types";

const I18n = I18nUtil.createScoped(Translations);

const DIMENSION_LABEL: Record<
  NonNullable<OnboardingV3PlanImpact>["dimensions"][number],
  string
> = {
  control: "Control",
  safety: "Safety",
  growth: "Growth",
};

/** Plan impact 装饰雷达 — Figma 固定等边三角示意；图表组件会隐藏具体分数，避免误认为真实得分 */
const PLAN_IMPACT_RADAR_SCORE = 72;

export interface PlanFitnessImpactStripProps {
  impact: OnboardingV3PlanImpact | null | undefined;
  /** Result 页不需要标题，直接把装饰雷达图标放进 Your Plan 内即可。 */
  hideTitle?: boolean;
}

/**
 * Plan Overview score impact strip — 布局/文案对齐 GoalFitnessImpact
 *（去掉 Complete this plan；delta 与 Potential… 上下排列；保留装饰雷达）
 */
export const PlanFitnessImpactStrip: FC<PlanFitnessImpactStripProps> = ({
  impact,
  hideTitle = false,
}) => {
  if (!impact || impact.delta <= 0) {
    return null;
  }

  return (
    <section
      className="flex flex-col gap-3"
      data-testid="plan-fitness-impact-strip"
      data-figma-node="97267:68609"
    >
      {hideTitle ? null : (
        <h3 className="headline_regular_s text-content_primary tracking-[-0.8px]">
          {I18n.t("What this plan does for your score")}
        </h3>
      )}

      <div className="flex items-center gap-4 overflow-clip rounded-2 bg-background_brand_overlay10 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col items-start">
            <span className="text-[36px] font-semibold leading-[44px] text-content_accent1_primary">
              +{impact.delta}
            </span>
            <span className="body_s text-content_tertiary">
              {I18n.t("Potential financial fitness score")}
            </span>
          </div>

          {impact.dimensions.length > 0 ? (
            <div className="flex flex-wrap items-center gap-[7px]">
              {impact.dimensions.map((dim) => (
                <span
                  key={dim}
                  className="inline-flex items-center gap-0.5 rounded-1 bg-background_solid_neutral_primary py-1 pl-1.5 pr-2 body_medium_s text-content_accent1_primary"
                  data-testid={`plan-impact-tag-${dim}`}
                >
                  <Icon
                    name="caret-up"
                    size={12}
                    className="text-content_accent1_primary"
                  />
                  {DIMENSION_LABEL[dim]}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <FitnessRadarChart
          total={PLAN_IMPACT_RADAR_SCORE}
          control={PLAN_IMPACT_RADAR_SCORE}
          safety={PLAN_IMPACT_RADAR_SCORE}
          growth={PLAN_IMPACT_RADAR_SCORE}
          size="plan-impact"
        />
      </div>
    </section>
  );
};
